import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Steps, Alert } from 'antd';
import { MailOutlined, NumberOutlined, LockOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import Auth from '../../../service/Auth';
import { useNotify, useWithNotify } from '../../../providers/NotificationProvider';

const passwordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[ !"#$%&'()*+,\-./:;<=>?@[\]\^_`{|}~]).{8,}$/;

const ForgotPassword = () => {
    const navigate = useNavigate();
    const location = useLocation();
    
    // ⭐ NUEVO: Obtener datos del state (desde ConfirmAccount)
    const emailFromState = location.state?.email || '';
    const returnTo = location.state?.returnTo || '/login';
    const needsConfirmation = location.state?.needsConfirmation || false;
    
    const [step, setStep] = useState(0); // 0 = email, 1 = confirm + new password
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState(emailFromState); // ⭐ Pre-cargar email si viene del state
    const [formEmail] = Form.useForm();
    const [formReset] = Form.useForm();
    
    const notify = useNotify();
    const withNotify = useWithNotify();

    // ⭐ NUEVO: Pre-cargar email en el formulario si viene del state
    useEffect(() => {
        if (emailFromState) {
            formEmail.setFieldsValue({ email: emailFromState });
        }
    }, [emailFromState, formEmail]);

    // 1) Solicitar código
    const handleRequestCode = async (values) => {
        setLoading(true);
        try {
            const emailValue = values.email.trim().toLowerCase();
            
            const response = await withNotify(
                Auth.forgotPassword(emailValue),
                {
                    loading: 'Enviando código...',
                    success: 'Código enviado. Revisa tu correo.',
                    error: 'No se pudo enviar el código'
                }
            );

            if (response.success === true) {
                setEmail(emailValue);
                setStep(1);
            } else {
                notify.error('No se pudo enviar el código. Intenta nuevamente.');
            }
        } catch (err) {
            console.error('Error enviando código:', err);
            notify.error(
                err?.response?.data?.message || 
                err?.error || 
                'No pudimos enviar el código. Intenta más tarde.'
            );
        } finally {
            setLoading(false);
        }
    };

    // 2) Confirmar y establecer nueva contraseña
    const handleConfirmReset = async (values) => {
        setLoading(true);
        try {
            const payload = {
                email,
                confirmationCode: String(values.code).trim(),
                newPassword: values.newPassword,
            };
            
            const response = await withNotify(
                Auth.confirmForgotPassword(payload.email, payload.confirmationCode, payload.newPassword),
                {
                    loading: 'Restableciendo contraseña...',
                    success: 'Contraseña actualizada correctamente',
                    error: 'No se pudo restablecer la contraseña'
                }
            );

            if (response.success === true) {
                formReset.resetFields();
                
                // ⭐ NUEVO: Lógica de redirección según contexto
                handleSuccess();
            } else {
                notify.error(response.message || 'No se pudo restablecer. Intenta nuevamente.');
            }

        } catch (err) {
            console.error('Error restableciendo contraseña:', err);
            notify.error(
                err?.response?.data?.message || 
                err?.message || 
                'No se pudo restablecer. Verifica el código e intenta nuevamente.'
            );
        } finally {
            setLoading(false);
        }
    };

    // ⭐ NUEVO: Manejador de éxito con lógica de redirección
    const handleSuccess = () => {
        if (needsConfirmation) {
            // Usuario viene desde confirmación de cuenta
            notify.success('Contraseña actualizada. Ahora confirma tu cuenta con el código de confirmación.');
            setTimeout(() => {
                navigate(returnTo, { state: { email: email } });
            }, 1500);
        } else {
            // Usuario normal recuperando contraseña
            notify.success('Contraseña actualizada exitosamente. Ya puedes iniciar sesión.');
            setTimeout(() => {
                navigate('/login');
            }, 1500);
        }
    };

    return (
        <div className="w-full flex items-center justify-center p-4">
            <div className="w-full max-w-xl">
                <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8">
                    <div className="mb-6">
                        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Recuperar contraseña</h1>
                        <p className="text-gray-600 mt-1">Sigue los pasos para restablecer tu contraseña.</p>
                    </div>

                    {/* ⭐ NUEVO: Alerta informativa si viene desde confirmación */}
                    {needsConfirmation && (
                        <Alert
                            message="Recuperación desde confirmación de cuenta"
                            description="Después de restablecer tu contraseña, deberás volver a la página de confirmación con el código original que te enviamos al registrarte."
                            type="info"
                            showIcon
                            className="mb-6"
                        />
                    )}

                    <Steps
                        current={step}
                        items={[
                            { title: 'Correo' },
                            { title: 'Código y nueva contraseña' },
                        ]}
                        className="mb-6"
                    />

                    {step === 0 && (
                        <Form
                            layout="vertical"
                            form={formEmail}
                            requiredMark={false}
                            onFinish={handleRequestCode}
                            validateTrigger={['onBlur', 'onChange']}
                        >
                            <Form.Item
                                name="email"
                                label={<span className="font-semibold text-gray-700">Correo electrónico</span>}
                                rules={[
                                    { required: true, message: 'El correo es requerido' },
                                    { type: 'email', message: 'Formato de correo inválido' },
                                ]}
                                hasFeedback
                            >
                                <Input
                                    size="large"
                                    prefix={<MailOutlined className="text-gray-400" />}
                                    placeholder="tu@ejemplo.com"
                                />
                            </Form.Item>

                            <Button
                                type="primary"
                                htmlType="submit"
                                loading={loading}
                                size="large"
                                className="w-full bg-primary hover:bg-primary/90 h-12 rounded-lg font-semibold"
                            >
                                Enviar código
                            </Button>

                            <div className="mt-4 text-center">
                                <Button
                                    type="link"
                                    onClick={() => {
                                        // ⭐ NUEVO: Volver a donde corresponda
                                        if (needsConfirmation) {
                                            navigate(returnTo, { state: { email: emailFromState } });
                                        } else {
                                            navigate('/login');
                                        }
                                    }}
                                    className="text-gray-600"
                                >
                                    {needsConfirmation ? 'Volver a confirmación' : 'Volver al inicio de sesión'}
                                </Button>
                            </div>
                        </Form>
                    )}

                    {step === 1 && (
                        <Form
                            layout="vertical"
                            form={formReset}
                            requiredMark={false}
                            onFinish={handleConfirmReset}
                            validateTrigger={['onBlur', 'onChange']}
                        >
                            {/* ⭐ NUEVO: Alerta recordatoria en paso 2 */}
                            {needsConfirmation && (
                                <Alert
                                    message="Recordatorio importante"
                                    description="Después de cambiar tu contraseña aquí, regresarás a la página de confirmación. Necesitarás el código de confirmación original (de 6 dígitos) que recibiste al registrarte."
                                    type="warning"
                                    showIcon
                                    className="mb-4"
                                />
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Form.Item
                                    label={<span className="font-semibold text-gray-700">Código de recuperación</span>}
                                    name="code"
                                    rules={[
                                        { required: true, message: 'Ingresa el código' },
                                        { len: 6, message: 'El código debe tener 6 dígitos' },
                                    ]}
                                    hasFeedback
                                    extra={
                                        <div className="mt-1 text-xs text-gray-500">
                                            Código de recuperación de contraseña (6 dígitos)
                                        </div>
                                    }
                                >
                                    <Input
                                        size="large"
                                        prefix={<NumberOutlined className="text-gray-400" />}
                                        placeholder="Ej: 123456"
                                        inputMode="numeric"
                                        maxLength={6}
                                    />
                                </Form.Item>

                                <Form.Item label={<span className="font-semibold text-gray-700">Correo</span>}>
                                    <Input size="large" value={email} disabled />
                                </Form.Item>
                            </div>

                            <Form.Item
                                label={<span className="font-semibold text-gray-700">Nueva contraseña</span>}
                                name="newPassword"
                                rules={[
                                    { required: true, message: 'La contraseña es requerida' },
                                    {
                                        pattern: passwordPattern,
                                        message: 'Mín 8, mayúscula, minúscula, número y símbolo.',
                                    },
                                ]}
                                hasFeedback
                            >
                                <Input.Password
                                    size="large"
                                    prefix={<LockOutlined className="text-gray-400" />}
                                    placeholder="Nueva contraseña"
                                />
                            </Form.Item>

                            <Form.Item
                                label={<span className="font-semibold text-gray-700">Confirmar contraseña</span>}
                                name="confirm"
                                dependencies={['newPassword']}
                                hasFeedback
                                rules={[
                                    { required: true, message: 'Confirma tu contraseña' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('newPassword') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Las contraseñas no coinciden'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    size="large"
                                    prefix={<LockOutlined className="text-gray-400" />}
                                    placeholder="Repite la contraseña"
                                />
                            </Form.Item>

                            <div className="flex flex-col sm:flex-row gap-3">
                                <Button
                                    size="large"
                                    className="h-12 rounded-lg flex-1"
                                    onClick={() => {
                                        setStep(0);
                                        formReset.resetFields();
                                    }}
                                >
                                    Volver
                                </Button>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                    size="large"
                                    className="h-12 rounded-lg flex-1 bg-primary hover:bg-primary/90 font-semibold"
                                >
                                    Restablecer contraseña
                                </Button>
                            </div>
                        </Form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForgotPassword;
